---
title: "Scanning books and ISBN auto-lookup"
slug: "scanning-books-isbn-lookup"
category: "books"
description: "Catalogue your library at the speed of beep — scan a barcode and BookBurrow fills in everything else."
tags: ["scanner", "isbn", "catalogue", "barcode", "books"]
order: 1
---

Cataloguing a school library used to be the worst job in teaching. You'd sit with a shoebox of index cards and write out every title, author, and publisher in longhand. A modern barcode scanner and BookBurrow's ISBN lookup turn a week of work into an afternoon: scan, click, move on.

## How ISBN lookup works

Every book printed since the 1970s carries an **ISBN** — a 10- or 13-digit number that uniquely identifies that edition. It's usually printed as a barcode on the back cover.

When you scan a book barcode in BookBurrow:

1. The scanner types the ISBN into the Add book form.
2. BookBurrow asks our ISBN service for that number.
3. The service checks **Open Library** (a free, open book database) and **Google Books** for a match.
4. The title, author, publisher, page count, and cover image come back.
5. BookBurrow saves the book into your catalogue with all of that pre-filled.

The first lookup for a given ISBN takes about a second. After that, BookBurrow remembers the result forever, so the second copy of the same book is instant.

<Callout variant="tip" title="Works for most — but not all — books">
Lookups succeed for around 90% of books in a typical primary school library. Older picture books, tiny independent presses, and reading scheme books (Oxford Reading Tree, Collins Big Cat) often don't have good ISBN metadata. For those, you type the title in by hand — see "Manual fallback" below.
</Callout>

## Scanning your first batch

<Steps>
  <Step title="Plug in your scanner">
    Any standard USB barcode scanner will do. Windows and Linux both recognise it as a keyboard, no drivers needed. See [Barcode scanner not working](/help/guides/barcode-scanner-not-working) if Windows doesn't pick it up.
  </Step>
  <Step title="Open Books → Add book">
    The cursor lands in the ISBN field automatically, ready for a scan.
  </Step>
  <Step title="Scan a book">
    Point the scanner at the ISBN barcode on the back cover and pull the trigger. You'll hear a beep, the number appears, and BookBurrow starts looking it up.
  </Step>
  <Step title="Check the result">
    Within a second or two, the form fills in with the title, author, and publisher. You can edit any field before saving.
  </Step>
  <Step title="Click Save and keep going">
    The form clears and the cursor is back in the ISBN field, ready for the next scan. A practised helper can catalogue about 100 books an hour this way.
  </Step>
</Steps>

## Where the scanned barcode actually gets stored

BookBurrow stores two separate things:

- **The ISBN**, which identifies the *edition* of the book. Many copies of the same title share one ISBN.
- **The item barcode**, which identifies the *physical copy*. This is usually a sticker you put inside the cover with a unique number.

If your library labels each book with its own barcode sticker, scan the ISBN first (BookBurrow fills in the metadata), then scan the item barcode in the **Item barcode** field to tie the physical copy to the record. If you haven't labelled books yet, you can use the ISBN itself as the item barcode for single-copy libraries — just be aware that if you ever get a second copy of that book, you'll need to re-label.

## Manual fallback

When ISBN lookup fails, you'll see "No match found for ISBN X — fill in manually?". Type in the title, author, and any other details. These still become a normal book record; they just missed the auto-fill.

Reading scheme books are a good example. An "Oxford Reading Tree Stage 4" book often has an ISBN but no lookup data. A practical approach is to set up a **book template** for each scheme (Settings → Books → Templates), so you just need to change the title and level per book.

## Speed tips

- **Don't type, scan.** Even if the lookup sometimes fails, the scanner is still the fastest way to capture the ISBN number itself.
- **Batch similar books together.** Do all the new picture books in one session, then all the chapter books. Switching between shelves kills your rhythm.
- **Put the scanner where you naturally reach for it.** Most people find it easier to hold a book in one hand and the scanner trigger in the other.
- **Use spine labels with barcodes.** The one-time effort of printing spine labels pays back on every scan-and-go loan later.

## Next steps

- **[Searching and filtering your catalogue](/help/guides/searching-and-filtering)** once you've got some books in.
- **[Handling lost or damaged books](/help/guides/handling-lost-or-damaged-books)** — the inevitable other side of a library.
